/**
 * FlexibleCRUD adapter for AI Studio agent instances (My instances list).
 */

import {
  deleteInstance,
  getInstance,
  listInstances,
  updateInstance,
} from '@/services/aiStudioAgentService';
import type { AiStudioAgentInstance, StudioBindingStatus } from '@/types/aiStudioAgent';

type ListParams = {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: StudioBindingStatus;
  setup?: '' | 'ready' | 'incomplete';
};

function matchesSearch(instance: AiStudioAgentInstance, term: string): boolean {
  const hay = [
    instance.instanceName,
    instance.agentName ?? '',
    instance.agentCode ?? '',
    instance.packName ?? '',
    instance.packCode ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(term);
}

function matchesFilters(instance: AiStudioAgentInstance, query: ListParams): boolean {
  // Default list hides archived unless status filter explicitly includes archived.
  if (query.status) {
    if (instance.status !== query.status) return false;
  } else if (instance.status === 'archived') {
    return false;
  }

  if (query.setup === 'ready' && instance.setupConfigured === false) return false;
  if (query.setup === 'incomplete' && instance.setupConfigured !== false) return false;

  return true;
}

function sortInstances(
  instances: AiStudioAgentInstance[],
  sortBy?: string,
  sortOrder: 'asc' | 'desc' = 'desc'
): AiStudioAgentInstance[] {
  if (!sortBy) return instances;
  const dir = sortOrder === 'asc' ? 1 : -1;
  return [...instances].sort((a, b) => {
    const av = a[sortBy as keyof AiStudioAgentInstance];
    const bv = b[sortBy as keyof AiStudioAgentInstance];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      return av.localeCompare(bv) * dir;
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

export const aiStudioInstancesCRUDService = {
  async getAll(params?: ListParams | string) {
    const query: ListParams = typeof params === 'string' ? { search: params } : params ?? {};

    let instances = await listInstances();
    instances = instances.filter((row) => matchesFilters(row, query));

    const term = query.search?.trim().toLowerCase();
    if (term) {
      instances = instances.filter((row) => matchesSearch(row, term));
    }

    instances = sortInstances(instances, query.sortBy ?? 'updatedAt', query.sortOrder ?? 'desc');

    const page = Math.max(1, query.page ?? 1);
    const limit = Math.max(1, query.limit ?? 20);
    const total = instances.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit;
    const data = instances.slice(start, start + limit);

    return {
      data,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
  },

  async getById(id: string) {
    return getInstance(id);
  },

  async update(id: string, data: Partial<AiStudioAgentInstance>) {
    return updateInstance(id, {
      instanceName: data.instanceName,
      industryPackId: data.industryPackId,
      status: data.status,
      instanceSetup: data.instanceSetup,
      autonomyFencesOverride: data.autonomyFencesOverride,
      toolConnections: data.toolConnections,
      memoryScope: data.memoryScope,
    });
  },

  async delete(id: string) {
    await deleteInstance(id);
  },
};
